import { cn, Icon, type SvgIcon } from '@bridebook/aura';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  NonNullableVariantProps,
  PolymorphicComponentPropsWithRef,
  PolymorphicRef,
} from '@bridebook/aura/src/lib/type-helpers';
import { AnchorHTMLAttributes, ElementType, forwardRef, ReactNode } from 'react';

const aVariants = cva(
  'relative cursor-pointer rounded-sm text-left font-semibold antialiased transition-all duration-150 hover:underline focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:outline-none active:underline disabled:pointer-events-none',
  {
    variants: {
      variant: {
        primary: 'text-indigo-crush disabled:text-indigo-crush/40',
      },
      size: {
        sm: 'inline-flex h-[16px] flex-nowrap items-center justify-center gap-2 text-[12px] leading-[100%] whitespace-nowrap',
        standard:
          'inline-flex h-[16px] flex-nowrap items-center justify-center gap-2 text-[14px] leading-[100%] whitespace-nowrap',
        lg: 'inline-flex h-[16px] flex-nowrap items-center justify-center gap-2 text-[16px] leading-[100%] whitespace-nowrap',
        inline: 'leading-inherit inline h-auto',
      },
      disabled: {
        true: 'pointer-events-none text-indigo-crush/40',
      },
    },
  },
);

// Extract the variant props type from CVA without null
type AVariantProps = NonNullableVariantProps<VariantProps<typeof aVariants>>;

interface PolymorphicA {
  <C extends ElementType = typeof defaultComponent>(
    props: PolymorphicComponentPropsWithRef<C, AProps>,
  ): ReactNode;
}

export interface AProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'disabled'>,
    VariantProps<typeof aVariants> {
  /**
   * Disable the link and its hover effects.
   */
  disabled?: boolean;
  /**
   * Icon to display on the left side of the button text.
   */
  icon?: SvgIcon;
  /**
   * Icon to display on the right side of the button text.
   */
  iconRight?: SvgIcon;
  /**
   * Button size. Choose `inherit` to match the font size of the surrounding text.
   */
  size?: AVariantProps['size'];
  /**
   * Style variant
   */
  variant?: AVariantProps['variant'];
}

const defaultComponent = 'a';

const _A = <C extends ElementType = typeof defaultComponent>(
  {
    as,
    className,
    children,
    variant = 'primary',
    size = 'standard',
    disabled,
    icon,
    iconRight,
    ...props
  }: PolymorphicComponentPropsWithRef<C, AProps>,
  ref: PolymorphicRef<C>,
) => {
  const Comp = as || defaultComponent;
  return (
    <Comp
      className={cn(aVariants({ variant, size, disabled, className }))}
      ref={ref}
      disabled={disabled}
      {...props}>
      {icon && <Icon icon={icon} size={16} />}
      {children}
      {iconRight && <Icon icon={iconRight} size={16} />}
    </Comp>
  );
};

export const A: PolymorphicA = forwardRef(_A);
